import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, InjectionToken, signal} from '@angular/core';
import {catchError, map, Observable, of, shareReplay, tap} from 'rxjs';
import {ServerEndpoints} from '../../server.endpoints';
import {DEFAULT_COMPANY_TIME_ZONE, isValidTimeZone} from './company-day';

/** Wire shape of `GET /company/CompanyTimeZone/Get`. */
export interface CompanyTimeZoneDto {
  timeZone: string;
}

/** The app registers the API origin under the plain string token `'BASE_URL'` (see `main.ts`). */
const BASE_URL = 'BASE_URL' as unknown as InjectionToken<string>;

/**
 * The company's configured IANA time zone — the reference for every calendar day in the web app
 * (see `company-day.ts`).
 *
 * Loaded ONCE per company session from `GET /company/CompanyTimeZone/Get`, which every authenticated company session
 * may call (Employee, Payroll, ShardAdmin, CompanyOwner, PlatformOwner in a company context). Concurrent callers
 * share the one in-flight request.
 *
 * Failure never falls back to the browser's zone: {@link timeZone} reads {@link DEFAULT_COMPANY_TIME_ZONE}
 * (the server's own default) until a real answer arrives. A failed load is NOT cached, so the next
 * {@link load} retries — a transient error must not pin a non-Berlin company to Berlin for the rest of the session.
 *
 * The cache is keyed by the session's company (the persisted `user` entry): the account switcher reloads the app
 * anyway, but a sign-out → sign-in into another company (or a payroll code redemption) does not, and must not keep
 * the previous company's zone. {@link reset} forgets the cache explicitly.
 */
@Injectable({providedIn: 'root'})
export class CompanyTimeZoneService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(BASE_URL);

  private readonly loadedZone = signal<string | undefined>(undefined);
  private inFlight: Observable<string> | undefined;
  private cachedFor: string | undefined;

  /** The company zone, or the server default until it has loaded. Never undefined, never the browser zone. */
  readonly timeZone = computed(() => this.loadedZone() ?? DEFAULT_COMPANY_TIME_ZONE);

  /** True once the server's answer is in (a fallback after a failed load does not count). */
  readonly loaded = computed(() => this.loadedZone() !== undefined);

  /**
   * Resolves the company zone. Emits once and completes; never errors (a failure emits the default zone).
   * Cached after the first successful answer, so callers may call this freely before every calendar computation.
   */
  load(): Observable<string> {
    const company = CompanyTimeZoneService.sessionCompanyKey();
    if (company !== this.cachedFor) {
      this.reset();
      this.cachedFor = company;
    }

    const known = this.loadedZone();
    if (known !== undefined) return of(known);

    this.inFlight ??= this.http.get<CompanyTimeZoneDto>(this.baseUrl + ServerEndpoints.Company_CompanyTimeZone_Get).pipe(
      map(dto => {
        // An id this engine cannot resolve would make every Intl call throw; use the server default instead.
        if (!isValidTimeZone(dto?.timeZone)) {
          console.error(`Unknown company time zone '${dto?.timeZone}', using ${DEFAULT_COMPANY_TIME_ZONE}`);
          return DEFAULT_COMPANY_TIME_ZONE;
        }
        return dto.timeZone;
      }),
      tap(zone => this.loadedZone.set(zone)),
      catchError(err => {
        console.error('Company time zone could not be loaded; using the default', err);
        this.inFlight = undefined;
        return of(DEFAULT_COMPANY_TIME_ZONE);
      }),
      shareReplay(1)
    );
    return this.inFlight;
  }

  /** Forgets the cached zone (e.g. after a company switch that does not reload the app). */
  reset(): void {
    this.loadedZone.set(undefined);
    this.inFlight = undefined;
    this.cachedFor = undefined;
  }

  /**
   * The company of the persisted session, or `''` when there is none. Reads `localStorage` directly: only WRITES
   * are consent-trapped (see `CookieService`), `getItem` is not.
   */
  private static sessionCompanyKey(): string {
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      return user?.company?.id ?? '';
    } catch {
      return '';
    }
  }
}
